import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Button, ConfirmationModal, CoreTeamAddModal, CoreTeamFormModal, CoreTeamStats, CoreTeamTable, LoadingSpinner, RejectedCoreTeamTable } from '../components'
import { API_BASE_URL, ConfirmationModalMeta, CoreTeamAddModalMeta, CoreTeamFormModalMeta, CoreTeamStatsMeta, CoreTeamTableMeta, LoadingSpinnerMeta, RejectedCoreTeamTableMeta } from '../data'
import { CoreTeam, CoreTeamMetaProps, RejectedCoreTeam, Stream, Volunteer } from '../data/types'
import { PlusIcon } from '../icons'
import { useAuth } from './auth'

type Props = {
    meta: CoreTeamMetaProps
}

export const CoreTeamPage = (props: Props) => {
    const { meta } = props
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [coreTeamMembers, setCoreTeamMembers] = useState<CoreTeam[]>([])
    const [rejectedEntries, setRejectedEntries] = useState<RejectedCoreTeam[]>([])
    const [error, setError] = useState<string | null>(null)
    const hasFetched = useRef(false)
    const [isAddModalOpen, setIsAddModalOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null)
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
    const [isUnapproveModalOpen, setIsUnapproveModalOpen] = useState(false)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [editingMember, setEditingMember] = useState<CoreTeam | null>(null)
    const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null)
    const [actionLoading, setActionLoading] = useState(false)

    // Filter state
    const [streamFilter, setStreamFilter] = useState<Stream | ''>('')
    const [tierFilter, setTierFilter] = useState<number | ''>('')
    const [statusFilter, setStatusFilter] = useState<'approved' | 'pending' | ''>('')
    const [activityStatusFilter, setActivityStatusFilter] = useState<'active' | 'on hold' | ''>('')
    const [duplicationsFilter, setDuplicationsFilter] = useState<boolean | null>(null)

    useEffect(() => {
        // Prevent duplicate fetches
        if (hasFetched.current) return
        hasFetched.current = true

        fetchCoreTeam()
        fetchRejectedEntries()
    }, [])

    const fetchCoreTeam = async () => {
        setLoading(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/core-team`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()
            let data = result.value || result

            // Handle double-nested arrays
            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            // Normalize boolean fields if needed
            const normalized = Array.isArray(data) ? data.map((item: any) => ({
                ...item,
                approved: item.approved === 1 || item.approved === true,
                volunteer: item.volunteer ? {
                    ...item.volunteer,
                    ismaili: item.volunteer.ismaili === 1 || item.volunteer.ismaili === true,
                    multi_faith_family: item.volunteer.multi_faith_family === 1 || item.volunteer.multi_faith_family === true,
                    registered_volunteer: item.volunteer.registered_volunteer === 1 || item.volunteer.registered_volunteer === true,
                    eligible_for_duty: item.volunteer.eligible_for_duty === 1 || item.volunteer.eligible_for_duty === true,
                    coreTeams: item.volunteer.core_teams || item.volunteer.coreTeams,
                    eventLeads: item.volunteer.event_leads || item.volunteer.eventLeads || [],
                    areasAsLead: item.volunteer.areas_as_lead || item.volunteer.areasAsLead || [],
                    areasAsDeputyLead: item.volunteer.areas_as_deputy_lead || item.volunteer.areasAsDeputyLead || [],
                    shiftsAsLead: item.volunteer.shifts_as_lead || item.volunteer.shiftsAsLead || [],
                    shiftsAsDeputyLead: item.volunteer.shifts_as_deputy_lead || item.volunteer.shiftsAsDeputyLead || []
                } : undefined,
                addedBy: item.addedBy || item.added_by
            })) : []

            setCoreTeamMembers(normalized)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while fetching core team members')
        } finally {
            setLoading(false)
        }
    }

    const fetchRejectedEntries = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/rejected/list`, { credentials: 'include' })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const result = await response.json()
            let data = result.value || result

            // Handle double-nested arrays
            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            setRejectedEntries(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error('Failed to fetch rejected entries:', err)
            // Don't set error for rejected entries - it's non-critical
        }
    }

    const handleSelectVolunteer = (volunteer: Volunteer) => {
        setSelectedVolunteer(volunteer)
        setIsFormModalOpen(true)
    }

    const handleSubmitCoreTeam = async (data: { volunteer_id: number, streams: Stream[], role: string, tier: number, notes?: string, status?: 'active' | 'on hold' }) => {
        const response = await fetch(`${API_BASE_URL}/core-team`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                ...data,
                added_by_volunteer_id: user.volunteer_csid
            })
        })

        if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.message || 'Failed to add core team member')
        }

        const result = await response.json()

        // Refresh the list
        await fetchCoreTeam()

        return result
    }

    const handleUpdateCoreTeam = async (data: { volunteer_id: number, streams: Stream[], role: string, tier: number, notes?: string, status?: 'active' | 'on hold' }) => {
        if (!editingMember) return

        const response = await fetch(`${API_BASE_URL}/core-team/${editingMember.id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(data)
        })

        if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.message || 'Failed to update core team member')
        }

        const result = await response.json()

        // Refresh the list
        await fetchCoreTeam()

        return result
    }

    const handleApprove = async (id: number) => {
        setSelectedMemberId(id)
        setIsApproveModalOpen(true)
    }

    const handleReject = async (id: number) => {
        setSelectedMemberId(id)
        setIsRejectModalOpen(true)
    }

    const handleUnapprove = async (id: number) => {
        setSelectedMemberId(id)
        setIsUnapproveModalOpen(true)
    }

    const handleEdit = async (member: CoreTeam) => {
        setEditingMember(member)
        setIsEditModalOpen(true)
    }

    const handleDelete = async (id: number) => {
        setSelectedMemberId(id)
        setIsDeleteModalOpen(true)
    }

    const confirmApprove = async () => {
        if (!selectedMemberId) return

        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/${selectedMemberId}/approve`, {
                method: 'PUT',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Update the local state
            setCoreTeamMembers(prev =>
                prev.map(member =>
                    member.id === selectedMemberId ? { ...member, approved: true } : member
                )
            )
            setIsApproveModalOpen(false)
            setSelectedMemberId(null)
        } catch (err) {
            console.error('Failed to approve core team member:', err)
            alert('Failed to approve core team member. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    const confirmReject = async () => {
        if (!selectedMemberId) return

        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/${selectedMemberId}/reject`, {
                method: 'PUT',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Refresh both lists to get updated data
            await fetchCoreTeam()
            await fetchRejectedEntries()
            setIsRejectModalOpen(false)
            setSelectedMemberId(null)
        } catch (err) {
            console.error('Failed to reject core team member:', err)
            alert('Failed to reject core team member. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    const confirmUnapprove = async () => {
        if (!selectedMemberId) return

        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/${selectedMemberId}/unapprove`, {
                method: 'PUT',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Update the local state
            setCoreTeamMembers(prev =>
                prev.map(member =>
                    member.id === selectedMemberId ? { ...member, approved: false } : member
                )
            )
            setIsUnapproveModalOpen(false)
            setSelectedMemberId(null)
        } catch (err) {
            console.error('Failed to unapprove core team member:', err)
            alert('Failed to unapprove core team member. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    const confirmDelete = async () => {
        if (!selectedMemberId) return

        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/${selectedMemberId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Remove from local state
            setCoreTeamMembers(prev => prev.filter(member => member.id !== selectedMemberId))
            setIsDeleteModalOpen(false)
            setSelectedMemberId(null)
        } catch (err) {
            console.error('Failed to delete core team member:', err)
            alert('Failed to delete core team member. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    const handleDeleteRejectedEntry = async (rejectedId: number) => {
        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/rejected/${rejectedId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Remove from local state
            setRejectedEntries(prev => prev.filter(entry => entry.id !== rejectedId))
        } catch (err) {
            console.error('Failed to delete rejected entry:', err)
            alert('Failed to delete rejected entry. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    const handleToggleStatus = async (id: number, newStatus: 'active' | 'on hold') => {
        setActionLoading(true)
        try {
            const response = await fetch(`${API_BASE_URL}/core-team/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ status: newStatus })
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()
            const updatedMember = result.model

            // Update local state
            setCoreTeamMembers(prev =>
                prev.map(member =>
                    member.id === id ? { ...member, status: updatedMember.status } : member
                )
            )
        } catch (err) {
            console.error('Failed to update status:', err)
            alert('Failed to update status. Please try again.')
        } finally {
            setActionLoading(false)
        }
    }

    // Bulk action handlers
    const handleBulkApprove = async (ids: number[]) => {
        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}/approve`, {
                method: 'PUT',
                credentials: 'include'
            })
        )

        await Promise.all(promises)
        await fetchCoreTeam() // Refresh the list
    }

    const handleBulkReject = async (ids: number[]) => {
        if (!window.confirm(`Are you sure you want to reject ${ids.length} core team member(s)? This action cannot be undone.`)) {
            return
        }

        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}/reject`, {
                method: 'PUT',
                credentials: 'include'
            })
        )

        await Promise.all(promises)
        await fetchCoreTeam() // Refresh the list
    }

    const handleBulkUnapprove = async (ids: number[]) => {
        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}/unapprove`, {
                method: 'PUT',
                credentials: 'include'
            })
        )

        await Promise.all(promises)
        await fetchCoreTeam() // Refresh the list
    }

    const handleBulkDelete = async (ids: number[]) => {
        if (!window.confirm(`Are you sure you want to delete ${ids.length} core team member(s)? This action cannot be undone.`)) {
            return
        }

        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            })
        )

        await Promise.all(promises)
        setCoreTeamMembers(prev => prev.filter(member => !ids.includes(member.id)))
    }

    const handleBulkPutOnHold = async (ids: number[]) => {
        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ status: 'on hold' })
            })
        )

        await Promise.all(promises)
        setCoreTeamMembers(prev =>
            prev.map(member =>
                ids.includes(member.id) ? { ...member, status: 'on hold' } : member
            )
        )
    }

    const handleBulkMakeActive = async (ids: number[]) => {
        const promises = ids.map(id =>
            fetch(`${API_BASE_URL}/core-team/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ status: 'active' })
            })
        )

        await Promise.all(promises)
        setCoreTeamMembers(prev =>
            prev.map(member =>
                ids.includes(member.id) ? { ...member, status: 'active' } : member
            )
        )
    }

    const existingVolunteerIds = coreTeamMembers.map(m => m.volunteer_id)
    const canAdd = user.permissions?.includes('add_core_team')
    const canApprove = user.permissions?.includes('approve_core_team')
    const canManage = user.permissions?.includes('manage_core_team')

    // Get user's approved streams from their core team entries
    const userApprovedStreams = React.useMemo(() => {
        if (!user?.volunteer_csid) return []
        const userCoreTeamEntries = coreTeamMembers.filter(
            entry => entry.volunteer_id === user.volunteer_csid && entry.approved
        )
        const allStreams = userCoreTeamEntries.flatMap(entry => entry.streams)
        return Array.from(new Set(allStreams)) as Stream[]
    }, [coreTeamMembers, user?.volunteer_csid])

    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                            {meta.description}
                        </p>
                        {canAdd && (
                            <div className="flex justify-center">
                                <Button onClick={() => setIsAddModalOpen(true)} size="lg">
                                    <PlusIcon className="w-5 h-5" />
                                    {meta.createButton}
                                </Button>
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {error && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && coreTeamMembers.length === 0 && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
                            <p className="text-xl">{meta.noDataMessage}</p>
                        </div>
                    )}

                    {!loading && !error && coreTeamMembers.length > 0 && (
                        <>
                            <CoreTeamStats
                                meta={CoreTeamStatsMeta}
                                data={coreTeamMembers}
                                streamFilter={streamFilter}
                                tierFilter={tierFilter}
                                statusFilter={statusFilter}
                                activityStatusFilter={activityStatusFilter}
                                duplicationsFilter={duplicationsFilter}
                            />
                            <CoreTeamTable
                                meta={CoreTeamTableMeta}
                                data={coreTeamMembers}
                                onApprove={handleApprove}
                                onReject={handleReject}
                                onUnapprove={handleUnapprove}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onToggleStatus={handleToggleStatus}
                                onBulkApprove={handleBulkApprove}
                                onBulkReject={handleBulkReject}
                                onBulkUnapprove={handleBulkUnapprove}
                                onBulkDelete={handleBulkDelete}
                                onBulkPutOnHold={handleBulkPutOnHold}
                                onBulkMakeActive={handleBulkMakeActive}
                                userHasApprovePermission={canApprove}
                                userHasManagePermission={canManage}
                                streamFilter={streamFilter}
                                tierFilter={tierFilter}
                                statusFilter={statusFilter}
                                activityStatusFilter={activityStatusFilter}
                                duplicationsFilter={duplicationsFilter}
                                onStreamFilterChange={(value: string) => setStreamFilter(value as Stream | '')}
                                onTierFilterChange={setTierFilter}
                                onStatusFilterChange={(value: string) => setStatusFilter(value as 'approved' | 'pending' | '')}
                                onActivityStatusFilterChange={(value: string) => setActivityStatusFilter(value as 'active' | 'on hold' | '')}
                                onDuplicationsFilterChange={() => {
                                    setDuplicationsFilter(prev => prev === null ? true : prev === true ? false : null)
                                }}
                            />
                        </>
                    )}

                    {/* Rejected Entries Section */}
                    {!loading && (
                        <div className="mt-12">
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                    {RejectedCoreTeamTableMeta.title}
                                </h2>
                                <p className="text-gray-600 dark:text-gray-400 mt-2">
                                    View rejected core team entries
                                </p>
                            </div>
                            <RejectedCoreTeamTable
                                meta={RejectedCoreTeamTableMeta}
                                data={rejectedEntries}
                            />
                        </div>
                    )}
                </div>
            </div>

            <CoreTeamAddModal
                meta={CoreTeamAddModalMeta}
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSelectVolunteer={handleSelectVolunteer}
                existingVolunteerIds={existingVolunteerIds}
            />

            <CoreTeamFormModal
                meta={CoreTeamFormModalMeta}
                isOpen={isFormModalOpen}
                volunteer={selectedVolunteer}
                currentUserName={user ? user.name : undefined}
                userHasManagePermission={canManage}
                userApprovedStreams={userApprovedStreams}
                onClose={() => {
                    setIsFormModalOpen(false)
                    setSelectedVolunteer(null)
                }}
                onSubmit={handleSubmitCoreTeam}
            />

            <CoreTeamFormModal
                meta={{...CoreTeamFormModalMeta, title: 'Edit Core Team Member', submitButton: 'Update', submittingButton: 'Updating...'}}
                isOpen={isEditModalOpen}
                volunteer={editingMember?.volunteer || null}
                currentUserName={user ? user.name : undefined}
                userHasManagePermission={canManage}
                userApprovedStreams={userApprovedStreams}
                onClose={() => {
                    setIsEditModalOpen(false)
                    setEditingMember(null)
                }}
                onSubmit={handleUpdateCoreTeam}
                initialData={editingMember ? {
                    streams: editingMember.streams,
                    role: editingMember.role,
                    tier: editingMember.tier,
                    notes: editingMember.notes,
                    status: editingMember.status
                } : undefined}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isApproveModalOpen}
                onCancel={() => {
                    if (!actionLoading) {
                        setIsApproveModalOpen(false)
                        setSelectedMemberId(null)
                    }
                }}
                onConfirm={confirmApprove}
                title="Approve Core Team Member"
                message="Are you sure you want to approve this core team member?"
                confirmButtonText="Approve"
                confirmButtonColor="success"
                loadingButtonText="Approving..."
                isLoading={actionLoading}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isRejectModalOpen}
                onCancel={() => {
                    if (!actionLoading) {
                        setIsRejectModalOpen(false)
                        setSelectedMemberId(null)
                    }
                }}
                onConfirm={confirmReject}
                title="Reject Core Team Member"
                message="Are you sure you want to reject and remove this core team member? This action cannot be undone."
                confirmButtonText="Reject & Remove"
                confirmButtonColor="danger"
                loadingButtonText="Rejecting..."
                isLoading={actionLoading}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isUnapproveModalOpen}
                onCancel={() => {
                    if (!actionLoading) {
                        setIsUnapproveModalOpen(false)
                        setSelectedMemberId(null)
                    }
                }}
                onConfirm={confirmUnapprove}
                title="Unapprove Core Team Member"
                message="Are you sure you want to unapprove this core team member? They will be moved back to submitted status."
                confirmButtonText="Unapprove"
                confirmButtonColor="warn"
                loadingButtonText="Unapproving..."
                isLoading={actionLoading}
            />

            <ConfirmationModal
                meta={ConfirmationModalMeta}
                isOpen={isDeleteModalOpen}
                onCancel={() => {
                    if (!actionLoading) {
                        setIsDeleteModalOpen(false)
                        setSelectedMemberId(null)
                    }
                }}
                onConfirm={confirmDelete}
                title="Delete Core Team Member"
                message="Are you sure you want to permanently delete this core team member? This action cannot be undone."
                confirmButtonText="Delete"
                confirmButtonColor="danger"
                loadingButtonText="Deleting..."
                isLoading={actionLoading}
            />
        </Fragment>
    )
}
